/**
 * Auth-store. Werkt in beide app-shells (superadmin + tenant).
 *
 * Zustand i.p.v. Pinia. Houdt alleen de huidige user + context lokaal —
 * de server-fetch zelf loopt via een TanStack-Query-hook (`useAuthMe`)
 * in M6, die deze store via `setUser` vult. Router-guards en imperative
 * code (zoals de axios-401-interceptor) lezen synchroon uit deze store.
 *
 * `login` / `fetchMe` / `logout` blijven hier behouden omdat ze door
 * niet-React contexten worden aangeroepen (login-form, logout-knop,
 * 401-redirect-recover) en synchroon de store moeten muteren.
 */
import { create } from 'zustand';
import { api, ensureCsrf } from '@shared/api/client';
import { useImpersonationStore } from '@shared/stores/impersonation';

export interface AuthUser {
  id: string;
  display_name: string;
  first_name?: string | null;
  last_name?: string | null;
  email: string;
  locale?: string;
  profile_picture_url?: string | null;
  mfa_enabled?: boolean;
  roles?: string[];
  /**
   * Effectieve permissies (issue 271). De SPA gaat rechten-gestuurd tonen waar
   * rechten gedelegeerd kunnen worden -- denk aan iemand die goedkeuringsflows mag
   * inzien maar niet aanpassen, zonder tenant-admin te zijn.
   */
  permissions?: string[];
  /**
   * Waar deze gebruiker iets te zoeken heeft (ADR-0037): `app` altijd, `manage` bij een
   * beheer-toewijzing. `permissions` hierboven zijn sindsdien de rechten van de host waarop
   * `/me` werd opgevraagd; het console-atoom staat op de app-host dus nooit in die lijst.
   * Wie wil weten of er een beheer-kant is, kijkt hier.
   */
  surfaces?: Array<'app' | 'manage'>;
  /**
   * Wat er op deze gebruiker wacht (ADR-0037, besluit 5). `pending` telt precies wat de
   * goedkeuringen-inbox toont; het menu-item *Goedkeuringen* en de badge hangen eraan. `open` telt
   * het beheer-overzicht *Alle goedkeuringen* (alle open taken in de omgeving): alleen op de
   * beheer-host met `approval.handle`, anders 0.
   */
  approvals?: { pending: number; open?: number };
  /**
   * De tellers achter *Nakijken*, *Presentie* en *Mijn vragenlijsten* (feedback 25): elk precies wat
   * die pagina toont, binnen het bereik van je recht. 0 waar het item er voor jou niet staat.
   */
  work?: { grading: number; attendance: number; questionnaires: number };
  /**
   * Wat er voor deze omgeving niet bestaat (module-laag, issue 1052): de paden van menu-items en de
   * sleutels van dashboardblokken van een module die uit staat. De SPA laat ze weg en weet niet
   * welke module erachter zit. Een item dat aan een recht hangt verdwijnt al via `permissions`.
   */
  hidden_menu_items?: string[];
  hidden_dashboard_blocks?: string[];
  /**
   * Aangewezen als nakijker van minstens één toets (besluit PO 16-10). Wie je aanwijst mag die toets
   * nakijken zonder het recht `test.grade`; de API laat hem binnen en toont alleen zijn eigen toetsen.
   * Uit als de toetsmodule uit staat. Lees hem via `mayGradeTests` (`@shared/mayGradeTests`), niet los.
   */
  is_designated_grader?: boolean;
  /** Wanneer true: dit is een superadmin die als shadow-tenant-admin werkt (ADR-0006). */
  is_superadmin_shadow?: boolean;
}

export type AuthContext = 'superadmin' | 'tenant';

/** Vorm van het `meta`-blok dat tenant-shell `/auth/me` meelevert (ADR-0020). */
export interface AuthMeMetaShadow {
  mode: 'superadmin-shadow-tenant-admin' | 'user-shadow' | null;
  impersonator: { id: string; display_name: string } | null;
  superadmin_origin: { id: string; display_name: string } | null;
  started_at: string | null;
}

interface AuthMeResponse {
  data: AuthUser;
  meta?: { shadow?: AuthMeMetaShadow };
}

/**
 * Wat `/auth/login` teruggeeft als tweestapsverificatie aanstaat (issue 21).
 *
 * Een **200** en geen 401: er is niets misgegaan, de login is alleen nog niet af.
 * Een foutcode zou het scherm laten zeggen dat het wachtwoord verkeerd was.
 */
interface MfaRequiredResponse {
  mfa_required: true;
  recovery_available: boolean;
}

type LoginResponse = AuthMeResponse | MfaRequiredResponse;

/** Wat `login()` de aanroeper vertelt: klaar, of er is nog een stap. */
export interface LoginResult {
  mfaRequired: boolean;
  recoveryAvailable?: boolean;
}

export interface ProfileWritable {
  name?: string;
  locale?: string;
}

interface AuthState {
  user: AuthUser | null;
  loading: boolean;
  /**
   * True nadat de eerste fetchMe() z'n round-trip heeft afgemaakt (success
   * of failure). Vóór deze flag mag geen guard naar /login redirecten —
   * anders zien refreshing-users kort een login-scherm tot /auth/me
   * terugkomt. Bij logout zetten we hem niet terug op false: de SPA
   * weet immers dat we expliciet uit zijn en wil meteen door naar /login.
   */
  hydrated: boolean;
  context: AuthContext;
  setContext: (next: AuthContext) => void;
  setUser: (next: AuthUser | null) => void;
  hasRole: (role: string) => boolean;
  hasPermission: (permission: string) => boolean;
  basePath: () => string;
  login: (email: string, password: string, remember?: boolean) => Promise<LoginResult>;
  /** De tweede stap afronden met een code uit de app of een herstelcode (issue 21). */
  completeMfa: (code: string) => Promise<AuthUser>;
  fetchMe: () => Promise<AuthMeResponse | null>;
  /**
   * Wis de lokale sessie-staat zonder een server-call. Aangeroepen door de
   * axios-401-interceptor: als de server-sessie weg is (verlopen, of na een
   * dev-reseed) moet de in-memory user meteen leeg — anders ziet `GuestOnly`
   * op /login nog de oude user en stuurt hij terug naar het dashboard (de
   * "catalogus stuitert terug"-bug).
   */
  clearSession: () => void;
  updateMe: (payload: ProfileWritable) => Promise<AuthUser>;
  /** Eigen profielfoto uploaden/verwijderen (issue #140) — tenant-only. */
  uploadMyAvatar: (file: File) => Promise<AuthUser>;
  deleteMyAvatar: () => Promise<AuthUser>;
  logout: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  loading: false,
  hydrated: false,
  context: 'tenant',
  setContext: (next) => set({ context: next }),
  setUser: (next) => set({ user: next }),
  hasRole: (role) => (get().user?.roles ?? []).includes(role),
  hasPermission: (permission) => (get().user?.permissions ?? []).includes(permission),
  basePath: () => (get().context === 'superadmin' ? '/api/v1/central/auth' : '/api/v1/auth'),
  async login(email, password, remember = false) {
    set({ loading: true });
    try {
      await ensureCsrf();
      const { data } = await api.post<LoginResponse>(`${get().basePath()}/login`, {
        email,
        password,
        remember,
      });

      // ⚠️ Een geslaagd wachtwoord is niet altijd een sessie (issue 21). Staat er
      // tweestapsverificatie aan, dan komt hier `{ mfa_required: true }` terug
      // zónder gebruiker — en er is op de server ook geen sessie. Zou dit blind
      // `data.data` wegschrijven, dan zet de SPA `user` op `undefined` en denkt
      // hij tegelijk dat er niemand is én dat de login klaar is.
      if ('mfa_required' in data && data.mfa_required) {
        return { mfaRequired: true, recoveryAvailable: Boolean(data.recovery_available) };
      }

      const me = data as AuthMeResponse;
      set({ user: me.data });
      // Impersonation-meta (alleen tenant-shell) doorzetten — server kan
      // namelijk ook bij login al een shadow-context aankondigen wanneer
      // de auth-user een superadmin-shadow-user is.
      useImpersonationStore.getState().hydrateFromMe(me);

      return { mfaRequired: false };
    } finally {
      set({ loading: false });
    }
  },
  async completeMfa(code) {
    set({ loading: true });
    try {
      const { data } = await api.post<AuthMeResponse>(
        `${get().basePath()}/mfa/challenge`,
        { code },
      );
      set({ user: data.data });
      useImpersonationStore.getState().hydrateFromMe(data);
      return data.data;
    } finally {
      set({ loading: false });
    }
  },
  async fetchMe() {
    set({ loading: true });
    try {
      const { data } = await api.get<AuthMeResponse>(`${get().basePath()}/me`);
      set({ user: data.data });
      useImpersonationStore.getState().hydrateFromMe(data);
      return data;
    } catch {
      set({ user: null });
      useImpersonationStore.getState().reset();
      return null;
    } finally {
      // `hydrated` flippt naar true ongeacht de uitkomst — guards mogen
      // pas redirecten nadat we minstens één keer een poging hebben
      // gedaan. Zonder dit ziet een refreshing tenant-admin de /login
      // flash terwijl /auth/me nog onderweg is.
      set({ loading: false, hydrated: true });
    }
  },
  clearSession: () => {
    set({ user: null });
    useImpersonationStore.getState().reset();
  },
  async updateMe(payload) {
    set({ loading: true });
    try {
      const { data } = await api.patch<{ data: AuthUser }>(`${get().basePath()}/me`, payload);
      set({ user: data.data });
      return data.data;
    } finally {
      set({ loading: false });
    }
  },
  async uploadMyAvatar(file) {
    set({ loading: true });
    try {
      const body = new FormData();
      body.append('avatar', file);
      // Via `basePath()`, want sinds issue 653 bestaat het endpoint aan beide
      // kanten: `/api/v1/auth/me/avatar` voor een tenant-user en
      // `/api/v1/central/auth/me/avatar` voor een superadmin. Hier stond het
      // tenant-pad hardgecodeerd met een comment dat superadmins geen foto
      // hebben — dat klopt niet meer.
      const { data } = await api.post<{ data: AuthUser }>(`${get().basePath()}/me/avatar`, body);
      set({ user: data.data });
      return data.data;
    } finally {
      set({ loading: false });
    }
  },
  async deleteMyAvatar() {
    set({ loading: true });
    try {
      const { data } = await api.delete<{ data: AuthUser }>(`${get().basePath()}/me/avatar`);
      set({ user: data.data });
      return data.data;
    } finally {
      set({ loading: false });
    }
  },
  async logout() {
    // BCS Login tenants get a front-channel logout URL back — we must navigate
    // the browser there so the IdP session ends too (ADR-0032).
    let redirect: string | undefined;
    try {
      const res = await api.post<{ redirect?: string }>(`${get().basePath()}/logout`);
      redirect = res?.data?.redirect;
    } catch {
      /* swallow — lokale staat altijd opschonen */
    }
    set({ user: null });
    useImpersonationStore.getState().reset();
    if (redirect) {
      window.location.assign(redirect);
    }
  },
}));
